'use client'

import { useRef, useEffect, useCallback, useState } from 'react'
import { useSession } from 'next-auth/react'
import { cn } from '@/lib/utils'
import { useAI } from '@/context/AIContext'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { CapabilityBubbles } from './CapabilityBubbles'
import type { AgentResponse } from '@/lib/agent-api'

interface ChatStreamPaneProps {
  isExpanded: boolean
}

export function ChatStreamPane({ isExpanded }: ChatStreamPaneProps) {
  const { data: session } = useSession()
  const {
    closeManager,
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
    currentConversationId,
    conversationContext,
    sendMessage,
    clarifySystem,
    viewMode,
    setViewMode,
    setRightPaneOpen,
    setActiveWidget,
    draftMessage,
    setDraftMessage,
    aiDefenseEnabled,
    setAiDefenseEnabled,
  } = useAI()


  const messagesEndRef = useRef<HTMLDivElement>(null)
  // Streaming state: accumulates tokens from SSE until 'done'
  const [streamingContent, setStreamingContent] = useState<string>('')
  const [isStreaming, setIsStreaming] = useState(false)
  const streamCleanupRef = useRef<(() => void) | null>(null)
  // Approval state: set when agent returns approval_required
  const [pendingApproval, setPendingApproval] = useState<{
    message: string
    action: Record<string, unknown> | null
    conversationId: string
  } | null>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory, isTyping])

  // Handle sending a message using SSE streaming
  const handleSendMessage = useCallback(async (content: string, isApproval: boolean = false) => {
    // Clear draft message after sending
    setDraftMessage('')
    
    // Add user message immediately
    if (!isApproval) {
      addMessage({ role: 'user', content })
    }
    setIsTyping(true)
    setStreamingContent('')
    setIsStreaming(false)

    try {
      const { agentApi } = await import('@/lib/agent-api')
      
      // For SSE streaming we need the conversation ID upfront.
      // First message: use non-streaming sendMessage (which also creates conversation in one call).
      // Subsequent messages: use SSE streaming with existing convId.
      let convId: string | null = null
      
      if (!currentConversationId) {
        // First message: create conversation with the initial message via non-streaming path
        // (SSE streaming isn't possible without a conversation ID)
        addMessage({ role: 'assistant', content: '', isLoading: true })
        
        const result = await sendMessage(content)
        if (result) {
          const response = result.agentResponse
          convId = result.conversationId
          
          if (response.type === 'completion') {
            addMessage({
              role: 'assistant',
              content: response.content || 'Operation completed.',
              toolCalls: response.tool_calls_executed,
              uiComponents: response.ui_components,
            })
            if (response.widget?.should_render) {
              setActiveWidget(response.widget)
              setRightPaneOpen(true)
            }
          } else if (response.type === 'clarification_required') {
            addMessage({
              role: 'assistant',
              content: response.clarification_message || 'I need some additional information.',
              missingParams: response.missing_params,
            })
          } else if (response.type === 'approval_required') {
            addMessage({
              role: 'assistant',
              content: response.approval_message || 'This action requires your approval. Please confirm or cancel.',
            })
            if (convId) {
              setPendingApproval({
                message: response.approval_message || 'Confirm action?',
                action: response.pending_action ?? null,
                conversationId: convId,
              })
            }
          } else if (response.type === 'error') {
            addMessage({ role: 'assistant', content: response.error_message || 'An error occurred.' })
          }
        }
        return
      }
      
      convId = currentConversationId
      
      // --- Step 2: Stream using SSE ---
      setIsStreaming(true)
      // Add a streaming placeholder message
      addMessage({ role: 'assistant', content: '...', isLoading: true })
      
      let accumulatedContent = ''
      
      const cleanup = agentApi.streamMessage(
        convId,
        content,
        { skipAiDefense: !aiDefenseEnabled, autoExecuteSafe: isApproval || conversationContext?.type === 'incident' },
        (event) => {
          if (event.type === 'thinking') {
            // Update streaming placeholder with thinking text
            setStreamingContent(event.content)
          } else if (event.type === 'token') {
            // Accumulate tokens into growing response
            accumulatedContent += event.content
            setStreamingContent(accumulatedContent)
          } else if (event.type === 'done' && event.agent_response) {
            // Stream complete - replace loading message with full response
            setIsStreaming(false)
            setStreamingContent('')
            setIsTyping(false)
            
            const resp = event.agent_response as AgentResponse
            if (resp.type === 'completion') {
              addMessage({
                role: 'assistant',
                content: resp.content || 'Operation completed.',
                toolCalls: resp.tool_calls_executed,
                uiComponents: resp.ui_components,
              })
              if (resp.widget?.should_render) {
                setActiveWidget(resp.widget)
                setRightPaneOpen(true)
              }
            } else if (resp.type === 'clarification_required') {
              addMessage({
                role: 'assistant',
                content: resp.clarification_message || 'I need some additional information.',
                missingParams: resp.missing_params,
              })
            } else if (resp.type === 'approval_required') {
              addMessage({
                role: 'assistant',
                content: resp.approval_message || 'This action requires your approval. Please confirm or cancel.',
              })
              if (convId) {
                setPendingApproval({
                  message: resp.approval_message || 'Confirm action?',
                  action: resp.pending_action ?? null,
                  conversationId: convId,
                })
              }
            } else if (resp.type === 'error') {
              addMessage({ role: 'assistant', content: resp.error_message || 'An error occurred.' })
            }
          } else if (event.type === 'error') {
            setIsStreaming(false)
            setStreamingContent('')
            setIsTyping(false)
            addMessage({ role: 'assistant', content: event.content || 'An error occurred.' })
          }
        }
      )
      
      streamCleanupRef.current = cleanup
      // Note: setIsTyping(false) is called inside the 'done' event handler above
      return
      
    } catch {
      addMessage({
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
      })
    } finally {
      if (!isStreaming) {
        setIsTyping(false)
      }
    }
  }, [addMessage, sendMessage, setIsTyping, setActiveWidget, setRightPaneOpen, setDraftMessage,
      currentConversationId, aiDefenseEnabled, conversationContext, isStreaming])
  
  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current()
      }
    }
  }, [])

  // Handle clarification submission
  const handleClarificationSubmit = useCallback(async (values: Record<string, string | number | boolean>) => {
    setIsTyping(true)
    
    try {
      let response = null
      
      // If it's a system selection, use clarifySystem
      if (values.system && typeof values.system === 'string') {
        response = await clarifySystem(values.system)
      }
      
      if (response) {
        // Handle the response from clarification
        if (response.type === 'completion') {
          addMessage({
            role: 'assistant',
            content: response.content || 'Operation completed.',
            toolCalls: response.tool_calls_executed,
            uiComponents: response.ui_components,
          })

          if (response.widget?.should_render) {
            setActiveWidget(response.widget)
            setRightPaneOpen(true)
          }
        } else if (response.type === 'error') {
          addMessage({
            role: 'assistant',
            content: response.error_message || 'An error occurred.',
          })
        }
      }
    } catch (error) {
      console.error('Clarification error:', error)
      addMessage({
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your clarification.',
      })
    } finally {
      setIsTyping(false)
    }
  }, [clarifySystem, addMessage, setIsTyping, setActiveWidget, setRightPaneOpen])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Get page name from context
  const getPageName = () => {
    if (currentPageContext === '/') return 'Dashboard'
    const segments = currentPageContext.split('/').filter(Boolean)
    return segments.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' > ')
  }

  // Toggle view mode
  const handleToggleExpand = () => {
    setViewMode(viewMode === 'collapsed' ? 'expanded' : 'collapsed')
  }

  // Handle approval confirmation or cancellation
  const handleApprovalResponse = useCallback(async (approved: boolean) => {
    if (!pendingApproval) return
    setPendingApproval(null)
    if (approved) {
      addMessage({ role: 'user', content: '✅ Confirmed — proceeding with the action.' })
      // Re-send as a new message so the agent executes the approved action
      await handleSendMessage('Yes, confirmed. Please proceed with the action.', true)
    } else {
      addMessage({ role: 'assistant', content: 'Action cancelled. Let me know if you need anything else.' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApproval, addMessage])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="relative border-b border-border/30 px-6 py-5 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* AI Icon */}
            <div className={cn(
              'flex h-12 w-12 items-center justify-center rounded-xl',
              'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
              'shadow-lg shadow-brand-purple/25',
              'ring-2 ring-white/50'
            )}>
              <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
            </div>
            
            <div>
              <h2 className="text-xl font-semibold text-brand-navy">
                Supervity AI
              </h2>
              <p className="text-sm text-muted-foreground">
                Your intelligent command center assistant
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Expand/Collapse Toggle */}
            {!isExpanded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleExpand}
                className="text-muted-foreground hover:text-foreground gap-1.5"
                title="Expand to Mission Control"
              >
                <Icons.maximize className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Expand</span>
              </Button>
            )}
            
            {chatHistory.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearHistory}
                className="text-muted-foreground hover:text-foreground gap-1.5"
                title="Clear conversation"
              >
                <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Clear</span>
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              onClick={closeManager}
              className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
              title="Close (Esc)"
            >
              <Icons.close className="h-5 w-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Page Context Badge */}
        {!isExpanded && (
          <div className="mt-4 flex items-center gap-3">
            <div className={cn(
              'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
              'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
              'text-brand-navy border border-brand-cornflower/20',
              'text-sm font-medium'
            )}>
              <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
              <span>Viewing: {getPageName()}</span>
            </div>
            
            <kbd className={cn(
              'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
              'text-xs font-medium',
              'border border-border/50 bg-muted/50 text-muted-foreground'
            )}>
              <Icons.command className="h-3 w-3 mr-1" />
              J to toggle
            </kbd>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="relative flex-1 overflow-y-auto px-6 py-5">
        {chatHistory.length === 0 ? (
          /* Empty State */
          <div className="flex h-full flex-col items-center justify-center text-center py-8">
            <div className={cn(
              'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
              'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
              'ring-1 ring-brand-cornflower/30'
            )}>
              <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
            </div>
            
            <h3 className="font-display text-xl font-semibold text-brand-navy">
              How can I help you?
            </h3>
            <p className="mt-2 max-w-sm text-muted-foreground">
              Ask me anything about your data, or use a quick action below.
            </p>

            {/* Quick Actions - Capability Bubbles */}
            <div className="mt-8 w-full max-w-lg">
              <CapabilityBubbles onSelect={handleQuickAction} />
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="space-y-4">
            {chatHistory.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                userName={session?.user?.name || undefined}
                userImage={session?.user?.image}
                onClarificationSubmit={handleClarificationSubmit}
              />
            ))}
            {/* Live streaming bubble — renders SSE tokens as they arrive */}
            {isStreaming && streamingContent && (
              <div className="flex items-start gap-3">
                <div className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                  'bg-gradient-to-br from-brand-purple to-brand-cornflower',
                  'shadow shadow-brand-purple/20'
                )}>
                  <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={2} />
                </div>
                <div className={cn(
                  'max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-3',
                  'bg-secondary border border-border/30',
                  'text-sm text-foreground/90 leading-relaxed'
                )}>
                  <span>{streamingContent}</span>
                  <span className="inline-block ml-0.5 w-1.5 h-3.5 bg-brand-purple/60 animate-pulse rounded-sm" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="relative border-t border-border/30">
        {/* AI Defense Toggle Bar */}
        <div className="flex items-center justify-between px-6 py-3 bg-gradient-to-r from-gray-50/50 to-transparent border-b border-border/20">
          <div className="flex items-center gap-2">
            <div className={cn(
              'flex items-center justify-center w-6 h-6 rounded-md',
              aiDefenseEnabled 
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            )}>
              <Icons.shield className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                Cisco AI Defense
              </span>
              <span className="text-xs text-muted-foreground">
                {aiDefenseEnabled ? 'Protected' : 'Disabled'}
              </span>
            </div>
          </div>
          
          <button
            onClick={() => setAiDefenseEnabled(!aiDefenseEnabled)}
            className={cn(
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-cornflower focus:ring-offset-2',
              aiDefenseEnabled ? 'bg-green-600' : 'bg-gray-300'
            )}
            role="switch"
            aria-checked={aiDefenseEnabled}
            title={aiDefenseEnabled ? 'Disable AI Defense' : 'Enable AI Defense'}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                aiDefenseEnabled ? 'translate-x-5' : 'translate-x-0'
              )}
            />
          </button>
        </div>
        
        {/* Approval Banner */}
        {pendingApproval && (
          <div className={cn(
            'mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3',
            'flex flex-col gap-2'
          )}>
            <div className="flex items-start gap-2">
              <Icons.alertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-amber-900 font-medium leading-snug">
                {pendingApproval.message}
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                onClick={() => handleApprovalResponse(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => handleApprovalResponse(true)}
              >
                ✓ Confirm
              </Button>
            </div>
          </div>
        )}
        <ChatInput
          onSend={handleSendMessage}
          disabled={isTyping || !!pendingApproval}
          placeholder={isTyping ? 'AI is thinking...' : pendingApproval ? 'Respond above to continue...' : 'Ask anything...'}
          initialValue={draftMessage}
          onValueChange={setDraftMessage}
        />
      </div>
    </div>
  )
}
